import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import { Crown, CreditCard, Check, ArrowRight, Zap } from 'lucide-react';
import { API_BASE_URL } from '../../utils/api';
import './MembershipGuard.css';

interface MembershipGuardProps {
  children: React.ReactNode;
  requireActiveMembership?: boolean;
}

interface MembershipStatus {
  tier_id: number | null;
  tier_name: string | null;
  status: 'active' | 'expired' | 'cancelled' | 'none';
  start_date: string | null;
  end_date: string | null;
  has_active_membership: boolean;
}

const MembershipGuard: React.FC<MembershipGuardProps> = ({ 
  children, 
  requireActiveMembership = true 
}) => {
  const { talent, isAuthenticated } = useTalentAuth();
  const [membershipStatus, setMembershipStatus] = useState<MembershipStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    checkMembershipStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, talent]);

  const checkMembershipStatus = async () => {
    if (!isAuthenticated || !talent) {
      setLoading(false);
      return;
    }

    try {
      setChecking(true);
      
      // Try to access a protected endpoint to check membership
      const token = localStorage.getItem('skillseek_talent_token');
      const response = await fetch(`${API_BASE_URL}/talent/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // If successful, user has active membership
        setMembershipStatus({
          tier_id: data.talent?.membership_tier_id || null,
          tier_name: data.talent?.membership_tier_name || null,
          status: data.talent?.membership_status || 'active',
          start_date: data.talent?.membership_start_date || null,
          end_date: data.talent?.membership_end_date || null,
          has_active_membership: true
        });
      } else if (response.status === 403 && data.requiresMembership) {
        // User doesn't have active membership
        setMembershipStatus({
          tier_id: null,
          tier_name: null,
          status: 'none',
          start_date: null,
          end_date: null,
          has_active_membership: false
        });
      } else {
        console.error('Error checking membership:', data);
        setMembershipStatus(null);
      }
    } catch (error) {
      console.error('Error checking membership status:', error);
      setMembershipStatus(null);
    } finally {
      setLoading(false);
      setChecking(false);
    }
  };

  // If not authenticated, don't show anything (let auth guard handle it)
  if (!isAuthenticated || !talent) {
    return null;
  }

  // Show loading state
  if (loading || checking) {
    return (
      <div className="membership-guard-loading">
        <div className="loading-spinner"></div>
        <p>Checking membership status...</p>
      </div>
    );
  }

  // If membership is required and user doesn't have it, show membership purchase page
  if (requireActiveMembership && membershipStatus && !membershipStatus.has_active_membership) {
    return <MembershipRequiredPage membershipStatus={membershipStatus} />;
  }

  // If all checks pass, render the protected content
  return <>{children}</>;
};

const MembershipRequiredPage: React.FC<{ membershipStatus: MembershipStatus }> = ({ 
  membershipStatus 
}) => {
  const navigate = useNavigate();

  const features = [
    'Create unlimited services',
    'Access to premium tools',
    'Priority customer support',
    'Advanced analytics',
    'Profile verification badge',
    'Featured in search results'
  ];

  return (
    <div className="membership-required-page">
      <div className="membership-required-container">
        <div className="membership-header">
          <div className="crown-icon">
            <Crown size={48} />
          </div>
          <h1>Membership Required</h1>
          <p className="subtitle">
            {membershipStatus.status === 'expired' 
              ? 'Your membership has expired. Renew to continue accessing your profile and services.'
              : 'You need an active membership to access this feature and start offering your services on SkillSeek.'
            }
          </p>
        </div>

        <div className="membership-benefits">
          <h2>
            <Zap size={24} />
            What you'll get with membership:
          </h2>
          <div className="benefits-grid">
            {features.map((feature, index) => (
              <div key={index} className="benefit-item">
                <Check size={16} className="check-icon" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="membership-status">
          {membershipStatus.status === 'expired' && (
            <div className="expired-notice">
              <h3>Membership Expired</h3>
              <p>
                Your {membershipStatus.tier_name} membership expired on{' '}
                {membershipStatus.end_date 
                  ? new Date(membershipStatus.end_date).toLocaleDateString()
                  : 'N/A'
                }
              </p>
            </div>
          )}
          
          {membershipStatus.status === 'cancelled' && (
            <div className="cancelled-notice">
              <h3>Membership Cancelled</h3>
              <p>Your membership was cancelled. Subscribe to a new plan to continue.</p>
            </div>
          )}
          
          {membershipStatus.status === 'none' && (
            <div className="no-membership-notice">
              <h3>No Active Membership</h3>
              <p>You haven't subscribed to any membership plan yet.</p>
            </div>
          )}
        </div>

        <div className="membership-actions">
          <button 
            onClick={() => navigate('/talent/membership/select')}
            className="select-membership-btn"
          >
            <CreditCard size={20} />
            {membershipStatus.status === 'expired' || membershipStatus.status === 'cancelled'
              ? 'Renew Membership'
              : 'Choose Membership Plan'
            }
            <ArrowRight size={16} />
          </button>
          
          <button 
            onClick={() => navigate('/talent/dashboard')}
            className="back-to-dashboard-btn"
          >
            Back to Dashboard
          </button>
        </div>

        <div className="help-section">
          <p>
            Need help choosing a plan?{' '}
            <a href="/support" className="help-link">
              Contact our support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MembershipGuard;
